import { getCookie } from 'cookies-next'

import getRequest from '@/client/utils/getRequest'
import { basePath } from '@/next.config'
import { clientGetToggleFromCookie } from '@/utils/cookies/getToggleFromCookie'
import { isAbortError } from '@/utils/isAbortError'

import {
	MAX_RETRY_ATTEMPTS,
	MAX_WAIT_BEFORE_RETRY_MS,
	WAIT_BEFORE_RETRY_MS,
} from './bulkDownloadToggles'

export type DownloadOptionCount = {
	count: number
	label: string
}

export type DownloadTrackStems = {
	id: number
	stems: TrackStem[] | null
}

export type DownloadOptions = {
	mixes: Track[]
	stems?: DownloadTrackStems[]
}

export type DownloadTrackRequest = {
	albumId: number
	albumTrackNumber: number
	fileType: string
	trackTitle: string
	mixVariation: string
}

export type DownloadTrackResponse = {
	expiresAt: string
	filename: string
	url: string
}

export type DownloadTrackLink = {
	fileName: string
	url: string
}

export type BulkDownloadTrackItem = Omit<DownloadTrackRequest, 'fileType'>

export type BulkDownloadStemItem = {
	albumId: number
	albumTrackNumber: number
	id: number
	name: string
	trackTitle: string
}

export type BulkDownloadSelection = {
	cutdowns: Track[]
	mixes: Track[]
	stems: BulkDownloadStemItem[]
	tracks: Track[]
}

export type BulkDownloadTrackRequest = {
	cutdowns: BulkDownloadTrackItem[]
	fileType: string
	mixes: BulkDownloadTrackItem[]
	stems?: BulkDownloadStemItem[]
	tracks: BulkDownloadTrackItem[]
	zipFileName: string
}

export type BulkDownloadTrackResponse = {
	expiresAt: string
	url: string
	zipFileName: string
}

export type BulkDownloadTrackLink = {
	fileName: string
	url: string
}

export type BulkDownloadZipProgress = {
	filesProcessed: number
	status?: 'preparing' | 'progress'
	totalFiles: number
}

type DownloadMixTrackResponse = Track & {
	MixType?: string | null
	MixVariation?: string | null
}

export const normaliseDownloadMixTrack = (track: Track): Track => {
	const { MixType, MixVariation, ...trackWithoutPascalFields } =
		track as DownloadMixTrackResponse

	return {
		...trackWithoutPascalFields,
		mixType: track.mixType ?? MixType ?? track.mixType,
		mixVariation: track.mixVariation ?? MixVariation ?? track.mixVariation,
	}
}

export const normaliseDownloadOptions = (
	options: DownloadOptions
): DownloadOptions => ({
	...options,
	mixes: options.mixes.map(normaliseDownloadMixTrack),
})

export const mapTrackToDownloadTrackRequest = (
	track: Track,
	fileType: string
): DownloadTrackRequest => ({
	albumId: Number(track.album.number),
	albumTrackNumber: Number(track.albumTrackNumber),
	fileType,
	mixVariation: track.mixVariation ?? '',
	trackTitle: track.title,
})

export const mapTrackToBulkDownloadTrackItem = (
	track: Track
): BulkDownloadTrackItem => ({
	albumId: Number(track.album.number),
	albumTrackNumber: Number(track.albumTrackNumber),
	mixVariation: track.mixVariation ?? '',
	trackTitle: track.title,
})

// Download API requires non-empty mixVariation on mixes; AudioMachine often has none.
const UNLABELLED_MIX_API_VARIATION = 'Mix'

const getMixVariation = (track: Track): string => {
	const mixVariation = track.mixVariation?.trim()

	if (mixVariation) {
		return mixVariation
	}

	return UNLABELLED_MIX_API_VARIATION
}

export const mapTrackToBulkMixItem = (track: Track): BulkDownloadTrackItem => ({
	albumId: Number(track.album.number),
	albumTrackNumber: Number(track.albumTrackNumber),
	mixVariation: getMixVariation(track),
	trackTitle: track.title,
})

// Download API value requires a non-empty mixVariation on cutdowns; unlabelled mixes have no mixType. This is not the display value, Locize handles the display value.
const UNLABELLED_CUTDOWN_API_VARIATION = 'Other'

const getCutdownMixVariation = (track: Track): string => {
	const mixType = track.mixType?.trim()

	if (mixType) {
		return mixType
	}

	return UNLABELLED_CUTDOWN_API_VARIATION
}

export const mapTrackToBulkCutdownItem = (
	track: Track
): BulkDownloadTrackItem => ({
	albumId: Number(track.album.number),
	albumTrackNumber: Number(track.albumTrackNumber),
	mixVariation: getCutdownMixVariation(track),
	trackTitle: track.title,
})

const getMainMixTrackId = (track: Track): number => {
	if (track.mix !== 1 && track.parentId) {
		return track.parentId
	}

	return track.id
}

const getMainMix = (track: Track, relatedTracks: Track[]): Track => {
	const mainMixTrackId = getMainMixTrackId(track)

	if (mainMixTrackId === track.id) {
		return track
	}

	const mainMix = relatedTracks.find(
		(candidate) => candidate.id === mainMixTrackId
	)

	return mainMix ?? track
}

const mapUniqueTrackStems = (
	track: Track,
	seenStemIds: Set<number>,
	relatedTracks: Track[]
): BulkDownloadStemItem[] => {
	const mainMix = getMainMix(track, relatedTracks)
	const stems: BulkDownloadStemItem[] = []

	for (const stem of track.stems ?? []) {
		if (seenStemIds.has(stem.id)) {
			continue
		}

		seenStemIds.add(stem.id)
		stems.push({
			albumId: Number(mainMix.album?.number),
			albumTrackNumber: Number(mainMix.albumTrackNumber),
			id: stem.id,
			name: stem.name,
			trackTitle: mainMix.title,
		})
	}

	return stems
}

export const mapTracksToBulkStemItems = (
	tracks: Track[],
	relatedTracks: Track[] = []
): BulkDownloadStemItem[] => {
	const seenStemIds = new Set<number>()
	const tracksForNaming = relatedTracks.length > 0 ? relatedTracks : tracks

	return tracks.flatMap((track) =>
		mapUniqueTrackStems(track, seenStemIds, tracksForNaming)
	)
}

export const mapTracksToBulkDownloadRequest = (
	selection: BulkDownloadSelection,
	fileType: string,
	zipFileName: string
): BulkDownloadTrackRequest => {
	const request: BulkDownloadTrackRequest = {
		cutdowns: selection.cutdowns.map(mapTrackToBulkCutdownItem),
		fileType,
		mixes: selection.mixes.map(mapTrackToBulkMixItem),
		tracks: selection.tracks.map(mapTrackToBulkDownloadTrackItem),
		zipFileName,
	}

	if (selection.stems.length === 0) {
		return request
	}

	return {
		...request,
		stems: selection.stems,
	}
}

export const getStemsDownloadSelection = async (
	track: Track
): Promise<BulkDownloadSelection> => {
	const downloadOptions = await clientGetDownloadOptions([track.id])
	const stemRecords =
		downloadOptions.stems?.find((entry) => entry.id === track.id)?.stems ?? []

	return {
		cutdowns: [],
		mixes: [],
		stems: mapTracksToBulkStemItems(
			[{ ...track, stems: stemRecords }],
			[track, ...downloadOptions.mixes]
		),
		tracks: [],
	}
}

export const clientGetDownloadOptions = async (
	trackIds: number[]
): Promise<DownloadOptions> => {
	const response = await getRequest<DownloadOptions>(
		`${basePath}/api/musicapp/downloads/options`,
		'Download Options',
		{
			body: JSON.stringify({ trackIds }),
			headers: {
				'Content-Type': 'application/json',
			},
			method: 'POST',
		}
	)

	if (response === undefined) {
		throw new Error('Failed to get download options')
	}

	return normaliseDownloadOptions(response)
}

export const getTrackDownloadEndpoint = (): string =>
	`${process.env.NEXT_PUBLIC_TRACK_DOWNLOAD_API_URL}/track`

export const getBulkTrackDownloadEndpoint = (): string =>
	`${process.env.NEXT_PUBLIC_TRACK_DOWNLOAD_API_URL}/track/bulk`

export const resolveBulkProgressUrl = (locationHeader: string): string => {
	const baseUrl = process.env.NEXT_PUBLIC_TRACK_DOWNLOAD_API_URL ?? ''

	return new URL(locationHeader, `${baseUrl}/`).toString()
}

type BulkSseEvent = {
	data: Record<string, unknown>
	type: string
}

const parseSseChunk = (chunk: string): BulkSseEvent[] => {
	const events: BulkSseEvent[] = []
	const blocks = chunk.split('\n\n')

	for (const block of blocks) {
		if (!block.trim()) {
			continue
		}

		let type = 'message'
		const dataLines: string[] = []

		for (const line of block.split('\n')) {
			if (line.startsWith('event:')) {
				type = line.slice('event:'.length).trim()
			} else if (line.startsWith('data:')) {
				dataLines.push(line.slice('data:'.length).trim())
			}
		}

		if (dataLines.length === 0) {
			continue
		}

		try {
			events.push({
				data: JSON.parse(dataLines.join('\n')) as Record<string, unknown>,
				type,
			})
		} catch {
			continue
		}
	}

	return events
}

const getZipProgressFromEvent = (
	event: BulkSseEvent
): BulkDownloadZipProgress | null => {
	if (event.type === 'preparing') {
		const { totalFiles } = event.data

		if (typeof totalFiles !== 'number') {
			return null
		}

		return {
			filesProcessed: 0,
			status: 'preparing',
			totalFiles,
		}
	}

	if (event.type !== 'progress') {
		return null
	}

	const { filesProcessed, totalFiles } = event.data

	if (typeof filesProcessed !== 'number' || typeof totalFiles !== 'number') {
		return null
	}

	return {
		filesProcessed,
		status: 'progress',
		totalFiles,
	}
}

export type WaitForBulkDownloadReadyOptions = {
	maxResumeAttempts?: number
	resumeBackoffMs?: number
	resumeOnDisconnect?: boolean
	sleep?: (milliseconds: number) => Promise<void>
}

const sleepFor = (milliseconds: number): Promise<void> =>
	new Promise((resolve) => {
		setTimeout(resolve, milliseconds)
	})

const throwIfAborted = (abortSignal?: AbortSignal): void => {
	if (abortSignal?.aborted) {
		throw new DOMException('The operation was aborted.', 'AbortError')
	}
}

const isClientErrorStatus = (statusCode: number): boolean =>
	statusCode >= 400 && statusCode < 500

const resolveReadyOrError = (
	event: BulkSseEvent
): BulkDownloadTrackResponse | null => {
	if (event.type === 'ready') {
		const url = event.data.url
		const zipFileName = event.data.zipFileName
		const expiresAt = event.data.expiresAt

		if (
			typeof url !== 'string' ||
			typeof zipFileName !== 'string' ||
			typeof expiresAt !== 'string'
		) {
			throw new Error('Failed to get bulk download url')
		}

		return {
			expiresAt,
			url,
			zipFileName,
		}
	}

	if (event.type === 'error') {
		const message =
			typeof event.data.message === 'string'
				? event.data.message
				: 'Failed to get bulk download url'

		throw new Error(message)
	}

	return null
}

const readBulkProgressStream = async (
	progressResponse: Response,
	onProgress?: (progress: BulkDownloadZipProgress) => void
): Promise<BulkDownloadTrackResponse | null> => {
	const processSseEvent = (
		event: BulkSseEvent
	): BulkDownloadTrackResponse | null => {
		const zipProgress = getZipProgressFromEvent(event)

		if (zipProgress) {
			onProgress?.(zipProgress)
		}

		return resolveReadyOrError(event)
	}

	if (progressResponse.body?.getReader) {
		const reader = progressResponse.body.getReader()
		const decoder = new TextDecoder()
		let buffer = ''
		let streamDone = false

		while (!streamDone) {
			const { done, value } = await reader.read()
			streamDone = done

			if (done) {
				break
			}

			buffer += decoder.decode(value, { stream: true })

			const completeBlocks = buffer.split('\n\n')
			buffer = completeBlocks.pop() ?? ''

			for (const event of parseSseChunk(`${completeBlocks.join('\n\n')}\n\n`)) {
				const result = processSseEvent(event)

				if (result) {
					await reader.cancel()
					return result
				}
			}
		}

		return null
	}

	const body = await progressResponse.text()

	for (const event of parseSseChunk(body)) {
		const result = processSseEvent(event)

		if (result) {
			return result
		}
	}

	return null
}

export const waitForBulkDownloadReady = async (
	progressUrl: string,
	authToken?: string,
	onProgress?: (progress: BulkDownloadZipProgress) => void,
	abortSignal?: AbortSignal,
	options: WaitForBulkDownloadReadyOptions = {}
): Promise<BulkDownloadTrackResponse> => {
	const resumeOnDisconnect =
		options.resumeOnDisconnect ??
		clientGetToggleFromCookie('BulkDownloadJobResume')
	const maxResumeAttempts = options.maxResumeAttempts ?? MAX_RETRY_ATTEMPTS
	const sleep = options.sleep ?? sleepFor
	let resumeBackoffMs = options.resumeBackoffMs ?? WAIT_BEFORE_RETRY_MS
	let attempt = 0

	const waitBeforeResumeAttempt = async (): Promise<void> => {
		throwIfAborted(abortSignal)
		await sleep(resumeBackoffMs)
		resumeBackoffMs = Math.min(resumeBackoffMs * 2, MAX_WAIT_BEFORE_RETRY_MS)
	}

	while (attempt < maxResumeAttempts) {
		throwIfAborted(abortSignal)
		attempt += 1

		let progressResponse: Response

		try {
			progressResponse = await fetch(progressUrl, {
				headers: {
					Accept: 'text/event-stream',
					...(authToken && { Authorization: `Bearer ${authToken}` }),
				},
				method: 'GET',
				...(abortSignal && { signal: abortSignal }),
			})
		} catch (error) {
			if (isAbortError(error)) {
				throw error
			}

			if (!resumeOnDisconnect || attempt >= maxResumeAttempts) {
				throw error instanceof Error
					? error
					: new Error('Failed to get bulk download url')
			}

			await waitBeforeResumeAttempt()
			continue
		}

		if (!progressResponse.ok) {
			if (
				resumeOnDisconnect &&
				!isClientErrorStatus(progressResponse.status) &&
				attempt < maxResumeAttempts
			) {
				await waitBeforeResumeAttempt()
				continue
			}

			throw new Error('Failed to get bulk download url')
		}

		const readyResponse = await readBulkProgressStream(
			progressResponse,
			onProgress
		)

		if (readyResponse) {
			return readyResponse
		}

		if (!resumeOnDisconnect || attempt >= maxResumeAttempts) {
			throw new Error('Failed to get bulk download url')
		}

		await waitBeforeResumeAttempt()
	}

	throw new Error('Failed to get bulk download url')
}

export const clientDownloadTrack = async (
	track: Track,
	fileType: string
): Promise<DownloadTrackLink> => {
	const authToken = getCookie('.auth', {}) as string | undefined

	const downloadResponse = await fetch(getTrackDownloadEndpoint(), {
		body: JSON.stringify(mapTrackToDownloadTrackRequest(track, fileType)),
		headers: {
			'Content-Type': 'application/json',
			...(authToken && { Authorization: `Bearer ${authToken}` }),
		},
		method: 'POST',
	})

	if (!downloadResponse.ok) {
		throw new Error('Failed to get download track url')
	}

	const { filename, url }: DownloadTrackResponse = await downloadResponse.json()

	return {
		fileName: filename,
		url,
	}
}

export const clientDownloadTracks = async (
	selection: BulkDownloadSelection,
	fileType: string,
	zipFileName: string,
	onProgress?: (progress: BulkDownloadZipProgress) => void,
	abortSignal?: AbortSignal
): Promise<BulkDownloadTrackLink> => {
	const authToken = getCookie('.auth', {}) as string | undefined

	const downloadResponse = await fetch(getBulkTrackDownloadEndpoint(), {
		body: JSON.stringify(
			mapTracksToBulkDownloadRequest(selection, fileType, zipFileName)
		),
		headers: {
			'Content-Type': 'application/json',
			...(authToken && { Authorization: `Bearer ${authToken}` }),
		},
		method: 'POST',
		...(abortSignal && { signal: abortSignal }),
	})

	if (!downloadResponse.ok) {
		throw new Error('Failed to queue bulk download')
	}

	const locationHeader = downloadResponse.headers.get('Location')

	if (!locationHeader) {
		throw new Error('Failed to get bulk download url')
	}

	const { url, zipFileName: responseZipFileName } =
		await waitForBulkDownloadReady(
			resolveBulkProgressUrl(locationHeader),
			authToken,
			onProgress,
			abortSignal
		)

	return {
		fileName: `${responseZipFileName}.zip`,
		url,
	}
}
